/**
 * Moteur de thèmes saisonniers et dynamiques pour ENOVADENT
 * Gère le calcul automatique des fêtes à dates fixes et variables (Pâques, Black Friday).
 */

/**
 * Calcule la date exacte du dimanche de Pâques (Algorithme de Meeus/Jones/Butcher)
 * Valide perpétuellement pour le calendrier grégorien.
 * @param {number} year 
 * @returns {Date}
 */
export function getEasterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = mars, 4 = avril
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month - 1, day);
}

/**
 * Période d'activation de Pâques :
 * Du lundi précédant Pâques au dimanche suivant Pâques (~14 jours).
 * @param {number} year 
 * @returns {{ start: Date, end: Date, easterSunday: Date }}
 */
export function getEasterThemeRange(year) {
    const easter = getEasterSunday(year);

    // Lundi précédent = Pâques - 6 jours
    const start = new Date(easter);
    start.setDate(easter.getDate() - 6);
    start.setHours(0, 0, 0, 0);

    // Dimanche suivant = Pâques + 7 jours
    const end = new Date(easter);
    end.setDate(easter.getDate() + 7);
    end.setHours(23, 59, 59, 999);

    return { start, end, easterSunday: easter };
}

/**
 * Période d'activation du Black Friday :
 * Du 4ème vendredi de novembre au dimanche soir suivant.
 * @param {number} year 
 * @returns {{ start: Date, end: Date }}
 */
export function getBlackFridayRange(year) {
    // Novembre est le mois 10 (0-indexed)
    let friday = new Date(year, 10, 1);
    while (friday.getDay() !== 5) {
        friday.setDate(friday.getDate() + 1);
    }
    // 4ème vendredi = 1er vendredi + 3 semaines (21 jours)
    friday.setDate(friday.getDate() + 21);
    friday.setHours(0, 0, 0, 0);

    const sunday = new Date(friday);
    sunday.setDate(friday.getDate() + 2);
    sunday.setHours(23, 59, 59, 999);

    return { start: friday, end: sunday };
}

/**
 * Configuration & métadonnées de chaque thème
 */
export const SEASONAL_THEMES = {
    default: {
        id: 'default',
        name: 'Classique E-novadent',
        shortName: 'Classique',
        icon: '🦷',
        secondaryIcon: '✨',
        greeting: 'Bienvenue sur ENOVADENT',
        description: 'Thème professionnel standard bleu et violet.',
        datesDescription: 'Toute l’année en dehors des périodes festives',
        colors: {
            primary: '#3a86ff',
            secondary: '#8338ec',
            accent: '#ff006e',
            backgroundLight: '#f8f9fa',
            glow: 'rgba(58, 134, 255, 0.25)'
        },
        decorationType: null
    },
    valentine: {
        id: 'valentine',
        name: 'Saint-Valentin',
        shortName: 'St-Valentin',
        icon: '💖',
        secondaryIcon: '💘',
        greeting: 'Joyeuse Saint-Valentin !',
        description: 'Tons chaleureux roses et rouges avec délicats cœurs flottants.',
        datesDescription: '10 février – 15 février',
        colors: {
            primary: '#e63946',
            secondary: '#c9184a',
            accent: '#ff4d6d',
            backgroundLight: '#fff5f7',
            glow: 'rgba(230, 57, 70, 0.25)'
        },
        decorationType: 'hearts'
    },
    easter: {
        id: 'easter',
        name: 'Pâques & Printemps',
        shortName: 'Pâques',
        icon: '🐣',
        secondaryIcon: '🌸',
        greeting: 'Joyeuses Pâques !',
        description: 'Tons printaniers vert tendre, lilas et jaune pastel.',
        datesDescription: 'Du lundi avant Pâques au dimanche suivant (14 jours)',
        colors: {
            primary: '#059669',
            secondary: '#8b5cf6',
            accent: '#f59e0b',
            backgroundLight: '#f4fbf7',
            glow: 'rgba(5, 150, 105, 0.25)'
        },
        decorationType: 'eggs'
    },
    aprilfools: {
        id: 'aprilfools',
        name: 'Poisson d’Avril',
        shortName: '1er Avril',
        icon: '🐟',
        secondaryIcon: '🎉',
        greeting: 'Joyeux 1er Avril ! Attention aux farces !',
        description: 'Ambiance pétillante avec petit poisson farceur.',
        datesDescription: '1er avril (24h)',
        colors: {
            primary: '#0284c7',
            secondary: '#059669',
            accent: '#f59e0b',
            backgroundLight: '#f0f9ff',
            glow: 'rgba(2, 132, 199, 0.25)'
        },
        decorationType: 'fish'
    },
    summer: {
        id: 'summer',
        name: 'Événement d’Été',
        shortName: 'Été & Plage',
        icon: '☀️',
        secondaryIcon: '🏖️',
        greeting: 'Bel été à toute l’équipe !',
        description: 'Bleu lagon, soleil vibrant et reflets estivaux.',
        datesDescription: '1er juillet – 31 août',
        colors: {
            primary: '#0284c7',
            secondary: '#d97706',
            accent: '#ea580c',
            backgroundLight: '#f0fdfa',
            glow: 'rgba(2, 132, 199, 0.25)'
        },
        decorationType: 'summer'
    },
    halloween: {
        id: 'halloween',
        name: 'Halloween',
        shortName: 'Halloween',
        icon: '🎃',
        secondaryIcon: '👻',
        greeting: 'Joyeux Halloween ! Des bonbons ou un sort ?',
        description: 'Orange citrouille, violet mystique et chauves-souris en fête.',
        datesDescription: '20 octobre – 2 novembre',
        colors: {
            primary: '#ea580c',
            secondary: '#7c3aed',
            accent: '#f97316',
            backgroundLight: '#faf5ff',
            glow: 'rgba(234, 88, 12, 0.3)'
        },
        decorationType: 'bats'
    },
    blackfriday: {
        id: 'blackfriday',
        name: 'Black Friday',
        shortName: 'Black Friday',
        icon: '🏷️',
        secondaryIcon: '⚡',
        greeting: 'Week-end Black Friday !',
        description: 'Dark mode élégant, contrastes forts or et néon.',
        datesDescription: 'Dernier week-end de novembre (vendredi au dimanche)',
        colors: {
            primary: '#111827',
            secondary: '#eab308',
            accent: '#ef4444',
            backgroundLight: '#f9fafb',
            glow: 'rgba(234, 179, 8, 0.3)'
        },
        decorationType: 'cyber'
    },
    christmas: {
        id: 'christmas',
        name: 'Noël & Fin d’Année',
        shortName: 'Noël & Nouvel An',
        icon: '🎄',
        secondaryIcon: '❄️',
        greeting: 'Joyeuses Fêtes de Fin d’Année !',
        description: 'Rouge festif, vert sapin, touches or et flocons de neige.',
        datesDescription: '1er décembre – 2 janvier',
        colors: {
            primary: '#dc2626',
            secondary: '#15803d',
            accent: '#d97706',
            backgroundLight: '#fef2f2',
            glow: 'rgba(220, 38, 38, 0.25)'
        },
        decorationType: 'snow'
    }
};

/**
 * Détecte automatiquement quel thème doit être actif selon la date passée en paramètre
 * @param {Date} date 
 * @returns {string} ID du thème actif
 */
export function getActiveSeasonalTheme(date = new Date()) {
    const year = date.getFullYear();
    const month = date.getMonth(); // 0 = janv, 1 = fév, ..., 11 = déc
    const day = date.getDate();

    // 1. Noël & Fêtes de fin d'année : 1er décembre au 2 janvier
    if ((month === 11 && day >= 1) || (month === 0 && day <= 2)) {
        return 'christmas';
    }

    // 2. Saint-Valentin : 10 février au 15 février
    if (month === 1 && day >= 10 && day <= 15) {
        return 'valentine';
    }

    // 3. Poisson d'Avril : 1er avril
    if (month === 3 && day === 1) {
        return 'aprilfools';
    }

    // 4. Pâques : Du lundi avant Pâques au dimanche suivant
    const easterRange = getEasterThemeRange(year);
    if (date >= easterRange.start && date <= easterRange.end) {
        return 'easter';
    }

    // 5. Été : 1er juillet au 31 août
    if (month === 6 || month === 7) {
        return 'summer';
    }

    // 6. Halloween : 20 octobre au 2 novembre
    if ((month === 9 && day >= 20) || (month === 10 && day <= 2)) {
        return 'halloween';
    }

    // 7. Black Friday : 4ème week-end de novembre
    const bfRange = getBlackFridayRange(year);
    if (date >= bfRange.start && date <= bfRange.end) {
        return 'blackfriday';
    }

    return 'default';
}
